import React, { useState, useEffect } from 'react';
import {
  FaCrown,
  FaUsers,
  FaHdd,
  FaRoute,
  FaFileAlt,
  FaEnvelope,
  FaShieldAlt,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';
import api from '../../../services/api';

const getProgressColor = (percentage) => {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
};

const getProgressTextColor = (percentage) => {
  if (percentage >= 90) return 'text-red-600';
  if (percentage >= 70) return 'text-yellow-600';
  return 'text-green-600';
};

const getTierStyles = (tier) => {
  const normalized = (tier || '').toLowerCase();
  switch (normalized) {
    case 'enterprise':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'professional':
    case 'pro':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'standard':
      return 'bg-slate-100 text-slate-800 border-slate-300';
    case 'free':
    case 'trial':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    default:
      return 'bg-primary-100 text-primary-800 border-primary-300';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const SubscriptionUsage = () => {
  const [tenant, setTenant] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [tenantRes, usageRes] = await Promise.all([
        api.tenants.getCurrent(),
        api.tenants.getUsage(),
      ]);
      setTenant(tenantRes.data.data);
      setUsage(usageRes.data.data);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription and usage data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const userPercentage =
    usage && usage.users.max > 0 ? Math.round((usage.users.current / usage.users.max) * 100) : 0;

  const storagePercentage =
    usage && usage.storage.maxMB > 0
      ? Math.round((usage.storage.usedMB / usage.storage.maxMB) * 100)
      : 0;

  const storageNotTracked = usage && usage.storage.usedMB === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Subscription & Usage
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              View your current plan details and resource usage
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Loading State */}
        {loading ? (
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
              <p className="mt-4 text-slate-600">Loading subscription data...</p>
            </div>
          </div>
        ) : error ? (
          /* Error State */
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <FaTimes className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">Failed to Load Data</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">{error}</p>
              <button
                onClick={fetchData}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Plan Card */}
            <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-amber-100">
                      <FaCrown className="w-7 h-7 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 font-medium mb-1">Current Plan</p>
                      <span
                        className={`inline-flex items-center px-4 py-1.5 rounded-full text-lg font-bold border ${getTierStyles(
                          tenant?.subscription_tier
                        )}`}
                      >
                        {tenant?.subscription_tier || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    {tenant?.tenant_name && (
                      <p className="text-sm text-slate-500">
                        Tenant:{' '}
                        <span className="font-medium text-slate-700">{tenant.tenant_name}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg px-4 py-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">
                      Expiry Date
                    </p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {formatDate(tenant?.expiry_date)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-4 py-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">
                      Data Retention
                    </p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {tenant?.data_retention_days != null
                        ? `${tenant.data_retention_days} days`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Meters */}
            <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Usage</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Current resource consumption against plan limits
                </p>
              </div>
              <div className="p-6 space-y-6">
                {/* Users Meter */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FaUsers className="text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Users</span>
                    </div>
                    <span
                      className={`text-sm font-semibold ${getProgressTextColor(userPercentage)}`}
                    >
                      {usage?.users.current} / {usage?.users.max}{' '}
                      <span className="text-slate-400 font-normal">({userPercentage}%)</span>
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(
                        userPercentage
                      )}`}
                      style={{ width: `${Math.min(userPercentage, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Storage Meter */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FaHdd className="text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Storage</span>
                    </div>
                    {storageNotTracked ? (
                      <span className="text-sm text-slate-400 italic">Not yet tracked</span>
                    ) : (
                      <span
                        className={`text-sm font-semibold ${getProgressTextColor(
                          storagePercentage
                        )}`}
                      >
                        {usage?.storage.usedMB} MB / {usage?.storage.maxMB} MB{' '}
                        <span className="text-slate-400 font-normal">({storagePercentage}%)</span>
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    {storageNotTracked ? (
                      <div className="h-3 rounded-full bg-slate-300 w-full opacity-30"></div>
                    ) : (
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(
                          storagePercentage
                        )}`}
                        style={{
                          width: `${Math.min(storagePercentage, 100)}%`,
                        }}
                      ></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Resource Counts */}
            <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Resources</h2>
                <p className="text-sm text-slate-600 mt-1">Active resources in your account</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 mb-3">
                      <FaRoute className="text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {usage?.resources.activeRoutes ?? 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 font-medium">Active Routes</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-100 mb-3">
                      <FaFileAlt className="text-emerald-600" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {usage?.resources.customSummaries ?? 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 font-medium">Custom Summaries</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 mb-3">
                      <FaEnvelope className="text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {usage?.resources.emailReports ?? 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 font-medium">Email Reports</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-violet-100 mb-3">
                      <FaShieldAlt className="text-violet-600" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {usage?.resources.customRoles ?? 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 font-medium">Custom Roles</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Flags */}
            {tenant?.feature_flags && Object.keys(tenant.feature_flags).length > 0 && (
              <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h2 className="text-lg font-bold text-slate-900">Feature Flags</h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Features available on your current plan
                  </p>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(tenant.feature_flags).map(([flag, enabled]) => (
                      <span
                        key={flag}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                          enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {enabled ? (
                          <FaCheck className="text-xs" />
                        ) : (
                          <FaTimes className="text-xs" />
                        )}
                        {flag
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, (s) => s.toUpperCase())
                          .trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionUsage;
